//import type {BffResponse} from '@esofertasv2/bff';
import { Spinner, styled } from '@volkswagen-onehub/components-core';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { Ofertas } from './components/ofertas';
import { OfertasContext } from './context/ofertas-context';
import { getDisclaimers, RegisteredDisclaimer } from './helpers/get-disclaimer';
import {
	getFinanciacionType,
	getPagoFinanciado,
	getPagoUnico,
} from './helpers/ofertas-helper';
import { useDisclaimerReference } from './helpers/use-disclaimer-reference';
import {
	CarlineWithOfertas,
	filtersFaContentOfertas,
} from './services/ofertas-service/models/ofertas';
import { useTrackingManager } from './tracking/use-tracking-manager';
import { setGlobalDisclaimer } from './utils/set-global-disclaimer';

const Content = styled.div`
	padding: 44px 31px;
	flex: auto;
	text-align: center;
	width: 100%;
	margin-top: 10%;

	@media screen and (min-width: 560px) {
		padding: 52px 46px;
	}

	@media screen and (min-width: 960px) {
		padding: var(--size-grid001);
	}
`;

export function Home(): JSX.Element {
	const {
		disclaimerManager,
		pageInfoService,
		breacrumbService,
		ofertasService,
		contentService,
		cmsService,
	} = useContext(OfertasContext);

	const [carlines, setCarlines] = useState<CarlineWithOfertas[]>([]);
	const [selectedGamaFilter, setSelectedGamaFilter] = useState<string | null>(
		null,
	);
	const [selectedMotorizacionFilter, setSelectedMotorizacionFilter] = useState<
		string | null
	>(null);

	// cambiamos este isFilterSelected (flag) a un useRef para evitar re-renders en mitad del filtrado
	const isFilterSelectedRef = useRef<boolean>(false);

	const [emptyCarlines, setEmptyCarlines] = useState<boolean>(false);
	const [disclaimers, setDisclaimers] = useState<RegisteredDisclaimer[]>([]);
	const [hasOneCarline, setHasOneCarline] = useState<boolean>(false);
	const [isCallMeNowOpen, setIsCallMeNowOpen] = useState<boolean>(false);

	// estado para saber si ya se hizo la primera carga inicial del servicio de ofertas
	const [isInitialLoadComplete, setIsInitialLoadComplete] =
		useState<boolean>(false);

	const filtersFaContentOfertas =
		contentService?.content as filtersFaContentOfertas;
	const trackingManager = useTrackingManager();
	const scopedDisclaimerManager =
		disclaimerManager.getScopedDisclaimerManager();

	useDisclaimerReference(disclaimers);
	setGlobalDisclaimer(scopedDisclaimerManager);

	const handleFilteredCarlines = (selectedButton: string) => {
		if (
			selectedGamaFilter === selectedButton ||
			selectedMotorizacionFilter === selectedButton
		) {
			return;
		}

		// setCarlines([]) // quitamos esto para impedir que el componente colapse a cero
		isFilterSelectedRef.current = true;
	};

	useEffect(() => {
		breacrumbService.set([]);
		pageInfoService.setPageInfo();
	}, [pageInfoService, breacrumbService]);

	// Carga inicial
	useEffect(() => {
		ofertasService
			.getOfertas(filtersFaContentOfertas)
			.then((carlines) => {
				setCarlines(carlines);
				const currentDisclaimers = getDisclaimers(carlines);

				if (carlines.length === 1) setHasOneCarline(true);
				if (carlines.length === 0) setEmptyCarlines(true);

				setDisclaimers(currentDisclaimers);
				setIsInitialLoadComplete(true); // aquí ya tenemos datos iniciales seguros
			})
			.catch((error) => {
				console.error('Error al obtener las ofertas:', error);
				setIsInitialLoadComplete(true);
			});

		ofertasService
			.getIsCallMeNowOpen()
			.then((isOpen) => {
				setIsCallMeNowOpen(isOpen);
			})
			.catch((error) => {
				console.error('Error al cargar status de Call Me Now:', error);
			});
	}, [filtersFaContentOfertas, ofertasService]);

	// filtrado
	useEffect(() => {
		if (!isInitialLoadComplete) return; // no filtramos de forma reactiva si la carga inicial no ha terminado

		const filteredCarlines = ofertasService.filterOfertas(
			selectedGamaFilter,
			selectedMotorizacionFilter,
		);

		setCarlines(filteredCarlines);

		if (filteredCarlines.length > 0) {
			const primerCoche = filteredCarlines[0];
			const pagoFinanciado = getPagoFinanciado(
				primerCoche.ofertas[0]?.datosEconomicos,
			);

			const trackingPayload = {
				CarlineId: primerCoche.code.toString(),
				CarlineName: primerCoche.title,
				SalesgroupName: primerCoche.ofertas[0]?.carline?.salesgroup.title,
				ModelId:
					primerCoche.ofertas[0]?.configuracion.motorizacion.code?.split(
						'-',
					)[0],
				ModelName: primerCoche.ofertas[0]?.carline?.salesgroup.model.name,
				ModelYear: primerCoche.ofertas[0]?.carline?.salesgroup.year.toString(),
				PriceRateType: pagoFinanciado
					? getFinanciacionType(pagoFinanciado)
					: undefined,
				PriceRateTotal: pagoFinanciado?.precio ?? undefined,
				PriceRateCurrency: 'EUR',
				PriceTotalTotal:
					getPagoUnico(primerCoche.ofertas[0]?.datosEconomicos)?.precio ??
					undefined,
				PriceTotalCurrency: 'EUR',
			};

			if (!isFilterSelectedRef.current) {
				trackingManager.trackOfertasGenericFeatureAppLoad(trackingPayload);
			} else {
				trackingManager.trackOfertasVehiclePageload(trackingPayload);
				isFilterSelectedRef.current = false; // reset flag
			}
		}
	}, [
		selectedGamaFilter,
		selectedMotorizacionFilter,
		ofertasService,
		isInitialLoadComplete,
		trackingManager,
	]);

	return (
		<>
			{cmsService.inEditor ? (
				<h1>Ofertas V2: cmsService.inEditor</h1>
			) : carlines.length > 0 ? (
				<Ofertas
					carlines={carlines}
					hasOneCarline={hasOneCarline}
					handleFilteredCarlines={handleFilteredCarlines}
					selectedGamaFilter={selectedGamaFilter}
					selectedMotorizacionFilter={selectedMotorizacionFilter}
					setSelectedGamaFilter={setSelectedGamaFilter}
					setSelectedMotorizacionFilter={setSelectedMotorizacionFilter}
					isCallMeNowOpen={isCallMeNowOpen}
				/>
			) : emptyCarlines ? (
				<></>
			) : (
				<Content>
					<Spinner variant="x-large" />
				</Content>
			)}
		</>
	);
}
